import React, { memo, useCallback, useEffect, useState } from 'react'
import { CardViewMode } from './components/CardViewMode'
import { TableViewMode } from './components/TableViewMode'
import { ISeachFilterData, SearchFilter } from './components/SearchFilter'

enum TextAlign {
  start = 'start',
  end = 'end',
  left = 'left',
  right = 'right',
  center = 'center',
  justify = 'justify',
  'match-parent' = 'match-parent'
}

interface HeaderProps {
  name?: string
  property: string
  format?: Function
  size?: number | string
  textAlign?: TextAlign | string
  component?: any
  isMobileHidden?: boolean
}

export interface BodyProps {
  [key: string]: any
}

interface CustomTableProps {
  header: Array<HeaderProps>
  body: Array<BodyProps>
  className?: string
  viewMode?: ModelOptions
  cardComponent?: Function
  itemsPerPage?: number
  propertiesToFilter?: string[]
  filterIsActive?: boolean
  filterPlaceholder?: string
  exportData?: (data: any) => void
  onClickItem?: (item: BodyProps) => void
  onFilteredDataChange?: (filteredData: any) => void
  fetchNextPage?: () => void
  isFetchingNextPage?: boolean
  totalItens?: number
  totalPages?: number
  isRemoteSearch?: boolean
  onSearchChange?: (value: string) => void
  searchValue?: string
  isLoading?: boolean
  isError?: boolean
  refetch?: () => void
}

export type ModelOptions = 'card' | 'table'

const getCurrentItems = (data: any[], page: number, itemsPerPage: number) => {
  const startIndex = page * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  return data.slice(startIndex, endIndex)
}

const CustomTableComponent: React.FC<CustomTableProps> = ({
  header,
  body = [],
  className,
  filterIsActive = false,
  itemsPerPage = 10,
  viewMode = 'table',
  onClickItem,
  exportData,
  cardComponent,
  propertiesToFilter,
  filterPlaceholder,
  onFilteredDataChange,
  fetchNextPage,
  totalPages,
  totalItens,
  isFetchingNextPage,
  isRemoteSearch = false,
  onSearchChange,
  searchValue = '',
  isLoading = false,
  isError = false,
  refetch
}) => {
  const [filteredData, setFilteredData] = useState<ISeachFilterData>()
  const [currentPage, setCurrentPage] = useState(0)
  const [cardItems, setCardItems] = useState<any[]>([])

  const currentData = filteredData?.filterValue ? filteredData?.filteredList : body
  const pages = totalPages || Math.ceil(currentData.length / itemsPerPage)
  const currentItems = getCurrentItems(currentData, currentPage, itemsPerPage)

  const getFilteredList = useCallback(
    (filteredData: ISeachFilterData) => {
      setFilteredData(filteredData)
      if (filteredData?.filterValue) {
        setCurrentPage(0)
      }

      if (onFilteredDataChange) {
        onFilteredDataChange(filteredData)
      }
    },
    [onFilteredDataChange]
  )

  const loadMoreCardItems = useCallback(() => {
    const nextItems = currentData.slice(cardItems.length, cardItems.length + itemsPerPage)
    setCardItems((prev) => [...prev, ...nextItems])
    setCurrentPage((prevPage) => prevPage + 1)
  }, [currentData, cardItems, itemsPerPage])

  const handleExportEvent = useCallback(() => {
    const dataToExport = viewMode === 'card' ? cardItems : filteredData?.filterValue ? filteredData.filteredList : body

    if (dataToExport.length && exportData) {
      exportData(dataToExport)
    }
  }, [viewMode, cardItems, filteredData, body, exportData])

  useEffect(() => {
    if (viewMode === 'card') {
      const itemsToLoad = (currentPage + 1) * itemsPerPage
      setCardItems(currentData.slice(0, itemsToLoad))
    }

    window.addEventListener('exportData', handleExportEvent)

    return () => {
      window.removeEventListener('exportData', handleExportEvent)
    }
  }, [viewMode, currentData, itemsPerPage, currentPage])

  useEffect(() => {
    const lastPageBasedOnCurrentData = Math.ceil(currentData.length / itemsPerPage) - 1

    if (fetchNextPage && currentPage > 0 && currentPage > lastPageBasedOnCurrentData) {
      fetchNextPage?.()
    }
  }, [currentPage, body, fetchNextPage, totalPages, itemsPerPage, currentData])

  return (
    <>
      {filterIsActive && (
        <SearchFilter
          filterPlaceholder={filterPlaceholder}
          listToFilter={body}
          getFilteredData={getFilteredList}
          propertiesToFilter={propertiesToFilter || []}
          handleInitialPage={() => setCurrentPage(0)}
          isRemoteSearch={isRemoteSearch}
          onSearchChange={onSearchChange}
          searchValue={searchValue}
        />
      )}

      {viewMode === 'card' ? (
        <CardViewMode
          filterIsActive={filterIsActive}
          loadMore={loadMoreCardItems}
          body={cardItems}
          cardComponent={cardComponent}
          setCurrentPage={setCurrentPage}
          pages={pages}
          currentPage={currentPage}
          onClickItem={onClickItem}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          totalItens={totalItens}
          isError={isError}
          refetch={refetch}
        />
      ) : (
        <TableViewMode
          header={header}
          filterIsActive={filterIsActive}
          body={currentItems}
          className={className}
          setCurrentPage={setCurrentPage}
          pages={pages}
          currentPage={currentPage}
          onClickItem={onClickItem}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          totalItens={totalItens}
          isError={isError}
          refetch={refetch}
        />
      )}
    </>
  )
}

export const CustomTable = memo(CustomTableComponent)
